import IconButton from '@mui/material/IconButton';
import React, { PureComponent, useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { ConnectedProps, connect } from 'react-redux';

import { removeAddress, requestEmailValidation, setAddress } from '@/actions/address';
import { notify } from '@/actions/ui';
import { EmailDeliveryHelp } from '@/componentsv2/EmailDeliveryHelp';
import { ErrAddressAlreadyTaken } from '@/core/address';
import { ERROR_CODES, apiErrorMessage, hasErrorCode } from '@/core/api';
import { RootState } from '@/store';
import { darkGreen, red500 } from '@/theme/colors';
import { isEmailValid } from '@/utils';

import Button from '../Button';
import { TextField } from '../FormAdapters';
import FAIcon from '../Icon/FAIcon';
import s from './EditEmails.module.scss';

interface EmailProps {
  address: string;
  verified: boolean;
  confirmed: boolean;
  primary: boolean;
  canRemove: boolean;
  userId: string;
  profileId: string;
  onSetPrimary: (address: string) => void;
  onRemove: (address: string) => void;
}

const emailConnector = connect(undefined, {
  requestEmailValidation,
  notify,
});

class Email extends PureComponent<EmailProps & ConnectedProps<typeof emailConnector>> {
  handleSetPrimary = () => {
    const { address, onSetPrimary } = this.props;
    if (onSetPrimary) {
      onSetPrimary(address);
    }
  };

  handleVerification = () => {
    this.resendValidation('verify').then(() => {
      this.props.notify('Verification email sent.');
    });
  };

  handleConfirmation = () => {
    this.resendValidation('confirm').then(() => {
      this.props.notify('Confirmation email sent.');
    });
  };

  resendValidation = (action: any) => {
    const { profileId, address, requestEmailValidation } = this.props;
    return requestEmailValidation(profileId, address, action);
  };

  handleRemove = () => {
    const { address, onRemove } = this.props;
    if (onRemove) {
      onRemove(address);
    }
  };

  render() {
    const { address, verified, confirmed, primary, canRemove, userId } = this.props;

    return (
      <div className={s.emailRoot}>
        <div className={s.email}>
          <span className={s.address} title={address}>
            {verified && confirmed && userId && (
              <FAIcon
                icon="check-circle"
                color={darkGreen}
                style={{ marginRight: 8 }}
                title="Verified"
              />
            )}
            {address}
          </span>
          <div className={s.badges}>
            {primary && userId && <div className={s.greenMessage}>Default</div>}
            {!verified && userId && <div className={s.grayMessage}>Unverified</div>}
            {verified && !confirmed && userId && <div className={s.grayMessage}>Unconfirmed</div>}
          </div>
        </div>
        <div className={s.actions}>
          {!verified && userId && (
            <div className={s.verificationAction}>
              <div>Verification sent</div>
              <Button
                color="secondary"
                variant="text"
                onClick={this.handleVerification}
                size="small"
              >
                Resend
              </Button>
            </div>
          )}
          {verified && !confirmed && userId && (
            <div className={s.verificationAction}>
              <div>Confirmation sent</div>
              <Button
                color="secondary"
                variant="text"
                onClick={this.handleConfirmation}
                size="small"
              >
                Resend
              </Button>
            </div>
          )}
          {!primary && verified && confirmed && userId && (
            <Button color="secondary" variant="text" onClick={this.handleSetPrimary} size="small">
              Make Default
            </Button>
          )}
          {canRemove && (
            <IconButton onClick={this.handleRemove} size="large">
              <FAIcon color={red500} icon="trash-o" size={14} />
            </IconButton>
          )}
        </div>
      </div>
    );
  }
}

const WrappedEmail = emailConnector(Email);

interface EditEmailProps {
  userId: string;
  profileId: string;
  showSuccessMessage?: boolean;
  /** Off when a dialog renders the help in its footer instead. */
  showDeliveryHelp?: boolean;
}

const editEmailConector = connect(
  (state: RootState, ownProps: EditEmailProps) => ({
    profile: state.profiles.fullProfiles[ownProps.profileId],
  }),
  {
    setAddress,
    removeAddress,
    notify,
  }
);

interface FormProps {
  email: string;
}

const EditEmails: React.FC<EditEmailProps & ConnectedProps<typeof editEmailConector>> = ({
  profileId,
  setAddress,
  userId,
  profile,
  removeAddress,
  notify,
  showSuccessMessage,
  showDeliveryHelp = true,
}) => {
  const validate = useCallback(
    (values: FormProps) => {
      const errors: { email?: string } = {};

      if (!values.email) {
        errors.email = 'Required';
      } else if (!isEmailValid(values.email)) {
        errors.email = 'Invalid email';
      } else if (
        profile.emails.some((e: any) => e.address.toLowerCase() === values.email.toLowerCase())
      ) {
        errors.email = 'Email already added';
      }

      return errors;
    },
    [profile.emails]
  );

  const onSubmit = useCallback(
    async (values: FormProps) => {
      const errors: { email?: string } = {};
      try {
        await setAddress(profileId, 'email', values.email);
        if (showSuccessMessage) notify('Email address added.');
      } catch (err: any) {
        if (err.message === ErrAddressAlreadyTaken.message) {
          errors.email = 'Email address is already in use.';
        } else {
          errors.email = 'An error occurred when adding the email address.';
        }
      }
      return errors;
    },
    [notify, profileId, setAddress, showSuccessMessage]
  );

  const handleSetPrimary = useCallback(
    (email: string) => {
      setAddress(profileId, 'email', email, true);
    },
    [profileId, setAddress]
  );

  const handleRemove = useCallback(
    async (email: string) => {
      try {
        await removeAddress(profileId, 'email', email);
      } catch (e: unknown) {
        if (hasErrorCode(e, ERROR_CODES.CONTACT_MUST_HAVE_PRIMARY)) {
          notify('User must have at least one primary email address', 'error');
        } else {
          notify(apiErrorMessage(e, 'An error occurred when removing the email address.'), 'error');
        }
      }
    },
    [notify, profileId, removeAddress]
  );

  const { emails } = profile;

  return (
    <div>
      <div className={s.root}>
        {emails.map((e: any) => {
          const canRemove = e.primary
            ? emails.filter((e: any) => e.confirmed && e.accepted).length > 1
            : emails.length > 1;
          return (
            <WrappedEmail
              key={e.address}
              userId={userId}
              profileId={profileId}
              {...e}
              onSetPrimary={handleSetPrimary}
              onRemove={handleRemove}
              canRemove={canRemove}
            />
          );
        })}
      </div>

      <Form
        onSubmit={onSubmit}
        validate={validate}
        render={({
          form,
          handleSubmit,
          submitErrors,
          submitting,
          dirtySinceLastSubmit,
          errors,
        }) => (
          <form
            onSubmit={async (values) => {
              const result = await handleSubmit(values);
              if (!result?.email) {
                form.restart();
              }
              return result;
            }}
            className="flex flex-wrap items-start"
          >
            <Field
              id="settingsEmailAddress"
              component={TextField}
              changeOnBlur={false}
              helperText={!dirtySinceLastSubmit && (errors?.email || submitErrors?.email)}
              type="email"
              name="email"
              label="Add email address"
              placeholder="Email address"
              style={{ width: 300, marginRight: 15 }}
            />
            <Button type="submit" size="medium" submitting={submitting} className="mt-18">
              Add
            </Button>
            {showDeliveryHelp && <EmailDeliveryHelp className="ml-auto mt-12" />}
          </form>
        )}
      />
    </div>
  );
};

export default editEmailConector(EditEmails);
